import { css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';
import { type FC, useMemo, useState } from 'react';

import { Button } from '@/shared/components/ui/Button';
import { FilterTag } from '@/shared/components/ui/FilterTag';
import { SearchBarNew } from '@/shared/components/ui/SearchBarNew';
import { ProductStatus } from '@/shared/hooks/queries/generated';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { ProductItemData } from '@/shared/types/product';

import { useProductFeedData } from '../../../features/analysisPageSellsHist/hooks/useProductFeedData';
import type { ProductFilterValues } from '../../../features/analysisPageSellsHist/types';

import { ProductFilterModal } from './ProductFilterModal';
import { ProductsGrid } from './ProductsGrid';
import { SellHistoryFilters as FiltersComponent } from './SellHistoryFilters';

interface ProductFeedProps {
  allProducts: ProductItemData[];
  status: ProductStatus;
  isLoading?: boolean;
}

export const ProductFeed: FC<ProductFeedProps> = ({ allProducts, status, isLoading = false }) => {
  const isMobile = useIsMobile();
  const [searchText, setSearchText] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterButtonRef, setFilterButtonRef] = useState<HTMLDivElement | null>(null);

  // Application des filtres et extraction des filtres disponibles
  const {
    products: baseProducts,
    availableBrands,
    availableConditions,
    filters,
    setFilters,
  } = useProductFeedData({
    allProducts,
    status,
  });

  // Filtrage client-side avec searchText uniquement
  const filteredProducts = useMemo(() => {
    let filtered = baseProducts;

    // Recherche par texte
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (product) => product.title?.toLowerCase().includes(search) || product.brand?.toLowerCase().includes(search),
      );
    }

    return filtered;
  }, [baseProducts, searchText]);

  const removeFilter = (...keys: (keyof ProductFilterValues)[]) => {
    const newFilters = { ...filters };
    keys.forEach((key) => delete newFilters[key]);
    setFilters(newFilters);
  };

  const handleExport = () => {
    const headers = ['Titre', 'Marque', 'Prix', 'État', 'Favoris', 'URL'];

    const rows = filteredProducts.map((product) => [
      product.title ?? '',
      product.brand ?? '',
      product.price ? product.price.toFixed(2) : '',
      product.itemCondition ?? '',
      product.favouriteCount?.toString() ?? '',
      product.url ?? '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) => (cell.includes(',') || cell.includes('"') ? `"${cell.replace(/"/g, '""')}"` : cell))
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Barre de recherche et filtres */}
      <div className={stack({ direction: 'column', gap: 'sm' })}>
        <div className={css({ display: 'flex', gap: '3', alignItems: 'center' })}>
          {/* Recherche */}
          <SearchBarNew placeholder="Search" value={searchText} onChange={setSearchText} />

          {/* Filters Button */}
          {!isMobile && (
            <div ref={setFilterButtonRef} className={css({ position: 'relative' })}>
              <Button
                handleClick={() => setShowFilterModal(!showFilterModal)}
                icon="add"
                className={css({ h: '2rem' })}
              >
                Filtres
              </Button>
              {/* Filter Modal */}
              <ProductFilterModal
                isOpen={showFilterModal}
                buttonRef={filterButtonRef}
                filters={filters}
                onFilterChange={setFilters}
                onClose={() => setShowFilterModal(false)}
              />
            </div>
          )}

          {/* Tags des filtres */}
          {(filters.priceMin !== undefined || filters.priceMax !== undefined) && (
            <FilterTag
              name="Prix"
              value={
                filters.priceMin !== undefined && filters.priceMax !== undefined
                  ? `${filters.priceMin} € - ${filters.priceMax} €`
                  : filters.priceMin !== undefined
                    ? `${filters.priceMin} € min.`
                    : `${filters.priceMax} € max.`
              }
              onRemove={() => removeFilter('priceMin', 'priceMax')}
            />
          )}

          {(filters.favoritesMin !== undefined || filters.favoritesMax !== undefined) && (
            <FilterTag
              name="Favoris"
              value={
                filters.favoritesMin !== undefined && filters.favoritesMax !== undefined
                  ? `${filters.favoritesMin} - ${filters.favoritesMax}`
                  : filters.favoritesMin !== undefined
                    ? `${filters.favoritesMin} min.`
                    : `${filters.favoritesMax} max.`
              }
              onRemove={() => removeFilter('favoritesMin', 'favoritesMax')}
            />
          )}

          {/* Bouton exporter */}
          <Button icon="download" handleClick={handleExport} className={css({ ml: 'auto' })}>
            Exporter
          </Button>
        </div>

        {/* Filtres de marque, état et tri */}
        <FiltersComponent
          filters={filters}
          availableBrands={availableBrands}
          availableConditions={availableConditions}
          onBrandChange={(brand) => setFilters({ ...filters, brand })}
          onConditionChange={(itemCondition) => setFilters({ ...filters, itemCondition })}
          onSortChange={(sort) => setFilters({ ...filters, sort })}
        />
      </div>

      {/* Nombre de produits */}
      <div
        className={css({
          fontSize: '12px',
          color: 'text.secondary',
          fontFamily: 'Manrope, sans-serif',
          fontWeight: '500',
          py: 'xs',
        })}
      >
        {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé
        {filteredProducts.length > 1 ? 's' : ''}
      </div>

      {/* Grille de produits */}
      <ProductsGrid
        key={`${status}-${filters.brand ?? 'all'}-${filters.itemCondition ?? 'all'}-${filters.sort ?? 'newest'}`}
        products={filteredProducts.slice(0, 500)} // Limiter à 500 pour la perf du rendu
        hasNextPage={false}
        fetchNextPage={() => {}}
        isFetchingNextPage={false}
        isLoading={isLoading}
      />
    </>
  );
};
